import * as React from 'react';

import { loadGoogleMaps } from '../utils/googleMaps';

/**
 * Drop-in replacement for `<LoadScript>` that routes through the shared loader.
 *
 * `<LoadScript>` injects its own script tag per mount, and the app's mounts disagreed
 * about `libraries` -- the activity editor asked for `places`, every map asked for
 * nothing. Two different script URLs in one SPA session is what Google reports as
 * "You have included the Google Maps JavaScript API multiple times". It also removes
 * the tag on unmount while leaving `window.google` in place, so remounting re-injects.
 *
 * Children are withheld until the API is usable, which is the invariant `<LoadScript>`
 * provided by construction: `@react-google-maps/api`'s `GoogleMap`/`Marker` reference
 * the bare `google` global, including in their unmount lifecycle. See
 * aidocs/lessons.md #1.
 */
const GoogleMapsReady = ({ apiKey, fallback = null, children }) => {
    // `google.maps` existing is the async bootstrap, not a usable Map.
    const [ready, setReady] = React.useState(() => typeof window.google?.maps?.Map === 'function');

    React.useEffect(() => {
        if (!apiKey || ready) return undefined;
        let mounted = true;
        loadGoogleMaps(apiKey)
            .then(() => mounted && setReady(true))
            .catch((e) => console.warn('GoogleMapsReady: Google Maps failed to load', e));
        return () => {
            mounted = false;
        };
    }, [apiKey, ready]);

    return ready ? <>{children}</> : fallback;
};

export default GoogleMapsReady;
